import { useEffect, useState, useRef, useCallback } from 'react';
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils';
import 'intl-tel-input/build/css/intlTelInput.css';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import palette from 'src/theme/palette';
import getDefaultCountryCode from 'src/utils/getDefaultCountryCode';
import useAuth from 'src/hooks/useAuth';
import { useFormContext } from 'react-hook-form';

export const DEFAULT_COUNTRY = 'EC';

const NANP_COUNTRIES = ['us', 'ca', 'pr', 'vi', 'gu', 'mp', 'as'];

const StyledInput = styled('div')(({ theme, error, inputStyles }) => ({
  marginTop: theme.spacing(1),
  width: '100%',
  '& > .iti': {
    width: '100%',
  },
  '& > .iti > .iti': {
    width: '100%',
  },
  input: {
    width: '100%',
    padding: theme.spacing(2),
    fontSize: 17,
    borderRadius: 8,
    borderWidth: '1px',
    borderColor: error ? palette.light.error.main : 'rgba(145, 158, 171, 0.24)',
    ...inputStyles,
  },
}));

const StyledMUIInput = styled('div')(({ theme, error, size, inputStyles }) => ({
  position: 'relative',
  width: '100%',
  '& > .iti': {
    width: '100%',
  },
  '& > .iti > .iti': {
    width: '100%',
  },
  '& input': {
    width: '100%',
    padding: size === 'small' ? '8.5px 14px' : '16.5px 14px',
    fontSize: size === 'small' ? '14px' : '16px',
    borderRadius: '4px',
    border: `1px solid ${
      error ? theme.palette.error.main : 'rgba(0, 0, 0, 0.23)'
    }`,
    backgroundColor: theme.palette.background.paper,
    fontFamily: theme.typography.fontFamily,
    lineHeight: 1.4375,
    '&:hover': {
      borderColor: error ? theme.palette.error.main : 'rgba(0, 0, 0, 0.87)',
    },
    '&:focus': {
      borderColor: error
        ? theme.palette.error.main
        : theme.palette.primary.main,
      borderWidth: '2px',
      outline: 'none',
    },
    '&:disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      color: 'rgba(0, 0, 0, 0.38)',
    },
    ...inputStyles,
  },
}));

const PhoneInputWithCountry = ({
  value,
  setValue,
  setError,
  error,
  countryCode,
  allowDropdown = true,
  disabled,
  inputProps = {},
  label,
  size = 'medium',
  allowEmpty = false,
}) => {
  const methods = useFormContext();
  const phoneInputRef = useRef(null);
  const [intl, setIntl] = useState(null);
  const [phoneErr, setPhoneErr] = useState(null);
  const hasError = error || phoneErr;
  const { user } = useAuth();
  const userSelectedCountryRef = useRef(false);
  const selectedCountryRef = useRef(null);

  useEffect(() => {
    const defaultCountry = countryCode ?? DEFAULT_COUNTRY;
    let intlInstance = intlTelInput(phoneInputRef.current, {
      initialCountry: defaultCountry,
      autoInsertDialCode: false,
      showSelectedDialCode: true,
      allowDropdown,
    });
    setIntl(intlInstance);

    const inputElement = phoneInputRef.current;
    const flagContainer = inputElement
      ?.closest('.iti')
      ?.querySelector('.iti__flag-container');

    let dropdownWasOpened = false;

    const handleDropdownClick = () => {
      dropdownWasOpened = true;
    };

    const handleCountryChange = () => {
      if (intlInstance) {
        const currentCountry = intlInstance.getSelectedCountryData()?.iso2;
        if (dropdownWasOpened) {
          userSelectedCountryRef.current = true;
          selectedCountryRef.current = currentCountry;
          dropdownWasOpened = false;
        }
      }
    };

    flagContainer?.addEventListener('click', handleDropdownClick);
    inputElement?.addEventListener('countrychange', handleCountryChange);

    return () => {
      flagContainer?.removeEventListener('click', handleDropdownClick);
      inputElement?.removeEventListener('countrychange', handleCountryChange);
      intlInstance?.destroy();
    };
  }, []);

  const checkAndSetError = useCallback(() => {
    if (!intl) {
      return;
    }

    if (allowEmpty && !phoneInputRef.current?.value.trim()) {
      setValue('');
      setError(null);
      setPhoneErr(null);
      return;
    }

    const isValid = intl.isValidNumberPrecise();
    if (!isValid) {
      setError('Invalid phone number');
      setPhoneErr('Invalid phone number');
    } else {
      setValue(intl.getNumber());
      setError(null);
      setPhoneErr(null);
    }
  }, [allowEmpty, intl, setValue, setError]);

  const countryBeforeInputRef = useRef(null);

  const handleBeforeInput = useCallback(() => {
    if (intl) {
      countryBeforeInputRef.current = intl
        .getSelectedCountryData()
        ?.iso2?.toLowerCase();
    }
  }, [intl]);

  const handleInputChange = useCallback(() => {
    if (!intl) {
      return;
    }

    const currentCountry = intl.getSelectedCountryData()?.iso2?.toLowerCase();
    const countryBeforeInput = countryBeforeInputRef.current;
    const manuallySelectedCountry = selectedCountryRef.current?.toLowerCase();

    if (
      userSelectedCountryRef.current &&
      manuallySelectedCountry &&
      currentCountry !== manuallySelectedCountry &&
      NANP_COUNTRIES.includes(currentCountry) &&
      NANP_COUNTRIES.includes(manuallySelectedCountry)
    ) {
      intl.setCountry(manuallySelectedCountry);
      return;
    }

    if (
      countryBeforeInput &&
      currentCountry !== countryBeforeInput &&
      NANP_COUNTRIES.includes(currentCountry) &&
      NANP_COUNTRIES.includes(countryBeforeInput)
    ) {
      intl.setCountry(countryBeforeInput);
    }
  }, [intl]);

  useEffect(() => {
    if (!intl) return;
    const location =
      user && !user.is_executive && !user.is_corporate
        ? { lat: user.lat, lng: user.lng, country: user?.country }
        : null;
    getDefaultCountryCode(
      (code) => {
        intl.setCountry(code);
        methods?.setValue('countryCode', code);
      },
      location,
      countryCode
    );
  }, [countryCode, intl, user, methods?.setValue]);
  if (label) {
    return (
      <FormControl fullWidth size={size} error={hasError} variant="outlined">
        <InputLabel
          shrink
          sx={{
            backgroundColor: 'background.paper',
            px: 0.5,
            ml: 1,
            transform: 'translate(14px, -9px) scale(0.75)',
            fontSize: size === 'small' ? '14px' : '16px',
            zIndex: 1,
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          {label}
        </InputLabel>
        <StyledMUIInput
          error={hasError}
          size={size}
          inputStyles={inputProps?.style}
        >
          <input
            type="text"
            aria-label={label}
            defaultValue={value}
            ref={phoneInputRef}
            onBlur={checkAndSetError}
            onKeyDown={handleBeforeInput}
            onInput={handleInputChange}
            disabled={disabled}
          />
        </StyledMUIInput>
        {hasError && <FormHelperText>{phoneErr || error}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <Box width="100%">
      <StyledInput error={hasError} inputStyles={inputProps?.style}>
        <input
          type="text"
          defaultValue={value}
          ref={phoneInputRef}
          onBlur={checkAndSetError}
          onKeyDown={handleBeforeInput}
          onInput={handleInputChange}
          disabled={disabled}
        />
      </StyledInput>
      {hasError ? (
        <Typography
          paragraph
          sx={{
            marginTop: (t) => t.spacing(1),
            marginX: (t) => t.spacing(2),
            textAlign: 'start',
          }}
          variant="caption"
          color="error.main"
        >
          {phoneErr || error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default PhoneInputWithCountry;
